import { CloudUpload } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Spinner } from '../../../../components/loaders/Spinner';
import { notify } from '../../../../notifications';
import { InputProps } from '../../base';
import styles from './styles.module.css';

/** Maps Bootstrap-style size to MUI Button size. */
function toMuiSize(size?: string): 'small' | 'medium' | 'large' {
  if (size === 'sm') return 'small';
  if (size === 'lg') return 'large';
  return 'medium';
}

interface Props extends Omit<InputProps, 'onChange' | 'size'> {
  multiple?: boolean;
  onChange: (files: File[]) => void;
  loading?: boolean;
  size?: string;
  maxSizeMb?: number;
  helperText?: React.ReactNode;
  /** Icon shown on the button when not loading. Defaults to CloudUpload. */
  startIcon?: React.ReactNode;
}

export type FileInputProps = Props;

export function FileInput(p: Props) {
  const { $t } = useIntl();
  const {
    name: passedName,
    onChange,
    accept,
    className,
    multiple = true,
    loading,
    size,
    maxSizeMb,
    helperText,
    startIcon,
  } = p;

  const name = passedName || String(Date.now());
  const buttonIcon = loading ? <Spinner /> : (startIcon ?? <CloudUpload />);

  return (
    <Box className={classNames(className, styles.wrapper)} display={'flex'} alignItems={'center'}>
      <Button
        component="label"
        htmlFor={name}
        variant="outlined"
        size={toMuiSize(size)}
        disabled={loading}
        startIcon={buttonIcon}
        sx={{ marginBottom: 0 }}
      >
        {p.label || (
          <FormattedMessage id="common.fileInput.btnText" defaultMessage="Seleccionar archivos" />
        )}
      </Button>
      <input
        type="file"
        multiple={multiple}
        id={name}
        name={name}
        onChange={(event) => {
          const files = event.target.files ? Array.from(event.target.files) : [];

          if (maxSizeMb && files.length && files.find((f) => f.size > maxSizeMb * 1024 * 1024)) {
            notify.error(
              $t(
                {
                  id: 'common.fileInput.maxSizeExceeded',
                  defaultMessage: 'Los archivos no deben superar {maxSizeMb} MB',
                },
                { maxSizeMb },
              ),
            );
            return;
          }

          onChange?.(files);
        }}
        accept={accept}
        disabled={loading}
      />
      {maxSizeMb && (
        <Box ml={1}>
          {$t(
            {
              id: 'common.fileInput.maxSizeLabel',
              defaultMessage: 'Máx. {maxSizeMb} MB',
            },
            { maxSizeMb },
          )}
        </Box>
      )}
      {helperText && <Box ml={1}>{helperText}</Box>}
    </Box>
  );
}
